import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../providers/LocaleProvider';
import { X, Send, MessageCircle, Loader2, Languages } from 'lucide-react';
import { chatService, ChatMessage } from '../services/chatService';
import { authService } from '../services/authService';
import { useHistoryBackClose } from '../hooks/useHistoryBackClose';
import { LEARNER_SIDE_BACKDROP_MS } from '../utils/learnerUiAnim';
import { useModalBackdropFade } from '../utils/learnerSlideModalMotion';
import { learnerSidePanelMotionClass, learnerSidePanelPlacement } from '../utils/learnerSidePanel';

interface ChatBotProps {
  isOpen: boolean;
  onClose: () => void;
  /** Fixed-position anchor for the chat toggle icon */
  anchorStyle?: React.CSSProperties;
}

export const ChatBot: React.FC<ChatBotProps> = ({ isOpen, onClose, anchorStyle }) => {
  const { t } = useTranslation('common');
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [shouldRender, setShouldRender] = useState(isOpen);

  useHistoryBackClose(isOpen, onClose, 'chatbot');
  const [isAnimatingOpen, setIsAnimatingOpen] = useState(false);
  const [preferredResponseLanguage, setPreferredResponseLanguage] = useState<string>('English');
  const [nationalityLanguage, setNationalityLanguage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const openTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  const getLanguageForNationality = (nationality?: string): string | null => {
    if (!nationality) return null;
    const key = nationality.trim().toLowerCase();
    const map: Record<string, string> = {
      russia: 'Russian',
      iran: 'Persian',
      afghanistan: 'Dari',
      tajikistan: 'Tajik',
      turkey: 'Turkish',
      germany: 'German',
      france: 'French',
      spain: 'Spanish',
      italy: 'Italian',
      japan: 'Japanese',
      china: 'Chinese',
      egypt: 'Arabic',
      'saudi arabia': 'Arabic',
      brazil: 'Portuguese',
      portugal: 'Portuguese',
      india: 'Hindi',
      pakistan: 'Urdu',
      'united kingdom': 'English',
      'united states': 'English',
      canada: 'English',
      australia: 'English'
    };

    return map[key] || null;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (openTimerRef.current) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    if (isOpen) {
      setShouldRender(true);
      setIsAnimatingOpen(false);
      // Guarantee a paint in "closed/offscreen" state before opening.
      openTimerRef.current = window.setTimeout(() => {
        setIsAnimatingOpen(true);
      }, 30);
      return;
    }

    setIsAnimatingOpen(false);
    // Match transition duration so unmount does not cut animation.
    closeTimerRef.current = window.setTimeout(() => {
      setShouldRender(false);
    }, LEARNER_SIDE_BACKDROP_MS + 40);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (openTimerRef.current) {
        window.clearTimeout(openTimerRef.current);
      }
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, [isOpen]);

  useEffect(() => {
    const user = authService.getCurrentUser();
    const mappedLanguage = getLanguageForNationality(user?.nationality || user?.location);
    setNationalityLanguage(mappedLanguage);
    setPreferredResponseLanguage('English');
  }, [isOpen]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: inputMessage.trim()
    };

    // Add user message immediately
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await chatService.sendMessage(
        userMessage.content,
        messages,
        preferredResponseLanguage
      );

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.message
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error: any) {
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: `${t('chat.error')} ${error.message || ''}`.trim()
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const adjustTextareaHeight = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  };

  useEffect(() => {
    if (inputRef.current) {
      adjustTextareaHeight(inputRef.current);
    }
  }, [inputMessage]);

  const backdropVisible = useModalBackdropFade(shouldRender, !isOpen);

  if (!shouldRender) return null;

  const { panelBottom, panelRight, panelLeft } = learnerSidePanelPlacement(anchorStyle, isRtl);
  const messagePlaceholder = t('chat.sendPlaceholder');

  return (
    <div className={`fixed inset-0 z-[62] flex items-end ${isRtl ? 'justify-start' : 'justify-end'} ${isAnimatingOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}>
      <div
        className={`learner-side-backdrop fixed inset-0 pointer-events-auto${backdropVisible ? ' is-visible' : ''}`}
        onClick={onClose}
      />
      <div
        className={`fixed flex h-[52vh] max-h-[52vh] w-[70vw] max-w-[70vw] flex-col rounded-2xl bg-white shadow-2xl pointer-events-auto dark:bg-gray-800 sm:h-[46vh] sm:max-h-[440px] sm:w-[60vw] sm:max-w-md ${learnerSidePanelMotionClass(isAnimatingOpen, isOpen, isRtl)}`}
        style={{
          bottom: panelBottom,
          ...(panelRight != null ? { right: panelRight } : {}),
          ...(panelLeft != null ? { left: panelLeft } : {}),
          ...(isOpen && !isAnimatingOpen
            ? { transform: isRtl ? 'translateX(-100%)' : 'translateX(100%)', opacity: 0.6 }
            : {})
        }}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-500 to-pink-500 rounded-t-2xl">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <MessageCircle className="w-6 h-6 text-white" />
              <h2 className="text-lg font-semibold text-white">{t('chat.title')}</h2>
            </div>
            {nationalityLanguage && nationalityLanguage !== 'English' && (
              <div className="mt-2">
                <button
                  onClick={() =>
                    setPreferredResponseLanguage((prev) =>
                      prev === 'English' ? nationalityLanguage : 'English'
                    )
                  }
                  className="px-2 py-1 rounded-lg bg-white/20 hover:bg-white/30 transition-colors text-white text-xs flex items-center gap-1"
                  title={t('chat.languageToggle', { language: preferredResponseLanguage })}
                >
                  <Languages className="w-4 h-4" />
                  {t('chat.preferredLanguage', { language: preferredResponseLanguage })}
                </button>
                <p className="text-[11px] text-white/85 mt-1">
                  {t('chat.nationalLanguageHint')}
                </p>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/20 transition-colors ml-2"
            aria-label={t('chat.closeChat')}
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="mt-8 text-center text-xs text-gray-500 dark:text-gray-400">
              <MessageCircle className="mx-auto mb-2 h-9 w-9 opacity-50" />
              <p>{t('chat.emptyStateApp')}</p>
            </div>
          )}
          
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                  message.role === 'user'
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl px-4 py-2">
                <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-end space-x-2">
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => {
                setInputMessage(e.target.value);
                adjustTextareaHeight(e.target);
              }}
              onKeyPress={handleKeyPress}
              placeholder={messagePlaceholder}
              rows={1}
              className="flex-1 resize-none overflow-y-auto rounded-xl border border-gray-300 px-4 py-2 text-xs placeholder:text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              style={{ minHeight: '44px', maxHeight: '120px' }}
              disabled={isLoading}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading}
              className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={t('chat.sendMessage')}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

